package com.zq.webproject.ui.earnings

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.zq.webproject.model.IncomeRecord
import com.zq.webproject.network.Repository
import com.zq.webproject.utils.TokenManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch

/**
 * 收益记录ViewModel
 * 管理收益记录的加载和显示
 * 支持下拉刷新和上拉加载更多
 */
class EarningsRecordViewModel {
    private val repository = Repository()
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    // 收益记录列表
    var incomeRecords by mutableStateOf<List<IncomeRecord>>(emptyList())
        private set

    // 加载状态
    var isLoading by mutableStateOf(false)
        private set
    
    // 下拉刷新状态
    var isRefreshing by mutableStateOf(false)
        private set
    
    // 上拉加载状态
    var isLoadingMore by mutableStateOf(false)
        private set

    // 错误信息
    var errorMessage by mutableStateOf<String?>(null)
        private set
    
    // 分页相关
    private var currentPage = 1
    private val pageSize = 10
    var hasMore by mutableStateOf(true)
        private set

    /**
     * 加载收益记录（首次加载）
     */
    fun loadIncomeRecords() {
        scope.launch {
            val token = TokenManager.getToken()
            if (token.isNullOrEmpty()) {
                println("EarningsRecordViewModel - Token为空，无法加载收益记录") // 日志输出
                errorMessage = "未登录，无法获取收益记录"
                return@launch
            }

            println("EarningsRecordViewModel - 开始加载收益记录，第${currentPage}页") // 日志输出
            isLoading = true
            errorMessage = null

            val result = repository.getIncomeRecords(token, currentPage, pageSize)

            if (result.isFailure) {
                val error = "获取收益记录失败: ${result.exceptionOrNull()?.message}"
                println("EarningsRecordViewModel - $error") // 日志输出
                errorMessage = error
                isLoading = false
                return@launch
            }

            val response = result.getOrNull()
            if (response?.code == 200 && response.data != null) {
                incomeRecords = response.data
                hasMore = response.data.size >= pageSize
                println("EarningsRecordViewModel - 收益记录加载成功，第${currentPage}页共 ${response.data.size} 条记录，是否有更多: $hasMore") // 日志输出

                // 打印前3条记录用于调试
                response.data.take(3).forEachIndexed { index, record ->
                    println("EarningsRecordViewModel - 记录${index + 1}: ID=${record.id}, 金额=${record.incomeAmount}, 订单号=${record.orderNo}") // 日志输出
                }
            } else {
                val errorMsg = response?.message ?: "数据为空"
                errorMessage = "获取收益记录失败: $errorMsg"
                println("EarningsRecordViewModel - $errorMessage") // 日志输出
            }

            isLoading = false
        }
    }
    
    /**
     * 下拉刷新
     */
    fun refresh() {
        scope.launch {
            val token = TokenManager.getToken()
            if (token.isNullOrEmpty()) {
                println("EarningsRecordViewModel - Token为空，无法刷新数据") // 日志输出
                isRefreshing = false
                return@launch
            }

            println("EarningsRecordViewModel - 开始下拉刷新") // 日志输出
            isRefreshing = true
            errorMessage = null
            currentPage = 1 // 重置页码

            val result = repository.getIncomeRecords(token, currentPage, pageSize)

            if (result.isFailure) {
                val error = "刷新失败: ${result.exceptionOrNull()?.message}"
                println("EarningsRecordViewModel - $error") // 日志输出
                errorMessage = error
            } else {
                val response = result.getOrNull()
                if (response?.code == 200 && response.data != null) {
                    incomeRecords = response.data
                    hasMore = response.data.size >= pageSize
                    println("EarningsRecordViewModel - 刷新成功，第${currentPage}页共 ${response.data.size} 条记录") // 日志输出
                } else {
                    errorMessage = "刷新失败: ${response?.message ?: "数据为空"}"
                    println("EarningsRecordViewModel - $errorMessage") // 日志输出
                }
            }

            isRefreshing = false
        }
    }
    
    /**
     * 加载更多数据
     */
    fun loadMore() {
        // 防止重复请求
        if (isLoadingMore || !hasMore) {
            println("EarningsRecordViewModel - 跳过加载更多: isLoadingMore=$isLoadingMore, hasMore=$hasMore") // 日志输出
            return
        }
        
        scope.launch {
            val token = TokenManager.getToken()
            if (token.isNullOrEmpty()) {
                println("EarningsRecordViewModel - Token为空，无法加载更多") // 日志输出
                return@launch
            }

            currentPage++
            println("EarningsRecordViewModel - 开始加载更多，第${currentPage}页") // 日志输出
            isLoadingMore = true

            val result = repository.getIncomeRecords(token, currentPage, pageSize)

            if (result.isFailure) {
                val error = "加载更多失败: ${result.exceptionOrNull()?.message}"
                println("EarningsRecordViewModel - $error") // 日志输出
                currentPage-- // 恢复页码
            } else {
                val response = result.getOrNull()
                if (response?.code == 200 && response.data != null) {
                    if (response.data.isNotEmpty()) {
                        // 追加新数据
                        incomeRecords = incomeRecords + response.data
                        hasMore = response.data.size >= pageSize
                        println("EarningsRecordViewModel - 加载更多成功，第${currentPage}页共 ${response.data.size} 条记录，总计 ${incomeRecords.size} 条") // 日志输出
                    } else {
                        hasMore = false
                        println("EarningsRecordViewModel - 没有更多数据了") // 日志输出
                    }
                } else {
                    println("EarningsRecordViewModel - 加载更多失败: ${response?.message}") // 日志输出
                    currentPage-- // 恢复页码
                }
            }

            isLoadingMore = false
        }
    }

    /**
     * 清除错误信息
     */
    fun clearError() {
        errorMessage = null
    }
}
